import { Play as PlayIcon } from '@components/icons'
import useThumbnail from '@lib/hooks/useThumbnail'
import cn from 'clsx'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import ReactPlayer from 'react-player'
import useMeasure from 'react-use-measure'

interface VideoPlayerProps {
  videoURl: string
  thumbnail?: string
  title?: string
  description?: any
  linkUrl?: string
  linkText?: string
  textAlign?: 'left' | 'center'
  aspectRatio: '16:9' | '9:16' | '4:3' | '21:9'
  displayText?: boolean
}

const VideoPlayer = ({
  videoURl,
  thumbnail,
  title,
  description,
  linkUrl,
  linkText,
  textAlign = 'left',
  aspectRatio = '16:9',
  displayText = true,
}: VideoPlayerProps) => {
  const [hasWindow, setHasWindow] = useState(false)
  const [playing, setPlaying] = useState(false)
  let [ref, { width, height }] = useMeasure()

  const switchAspectRatio = () => {
    switch (aspectRatio) {
      case '21:9':
        return {
          container: 'aspect-[21/9]',
          videoHeight: (width * 9) / 21,
        }
      case '16:9':
        return {
          container: 'aspect-[16/9]',
          videoHeight: (width * 9) / 16,
        }
      case '9:16':
        return {
          container: 'aspect-[16/9]',
          videoHeight: height,
        }
      case '4:3':
        return {
          container: 'aspect-[4/3]',
          videoHeight: (width * 3) / 4,
        }
      default:
        return {
          container: 'aspect-[16/9]',
          videoHeight: (width * 9) / 16,
        }
    }
  }

  const thumbnailUrl = useThumbnail(videoURl, {
    time: 0.3,
    width: width,
    height: switchAspectRatio().videoHeight,
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHasWindow(true)
    }
  }, [])

  return (
    <>
      <div
        ref={ref}
        className={cn(`${switchAspectRatio().container} w-full`, {
          'flex justify-center bg-brand-grey': aspectRatio === '9:16',
          'bg-filter-grey': !playing && aspectRatio !== '9:16',
        })}
      >
        {hasWindow && (
          <ReactPlayer
            light={thumbnail || thumbnailUrl || true}
            controls
            playing
            onPlay={() => setPlaying(true)}
            width={aspectRatio !== '9:16' ? width : (height * 9) / 16}
            height={switchAspectRatio().videoHeight}
            playIcon={<PlayIcon />}
            url={videoURl}
          />
        )}
      </div>
      {displayText && (
        <div
          className={cn(
            'flex flex-col justify-center gap-3 text-dark-grey',
            {
              'items-center text-center': textAlign === 'center',
            },
            {
              'items-start text-left': textAlign === 'left',
            }
          )}
        >
          {title && (
            <div className="heading-mobile-3 md:heading-3 pt-6 md:pt-10">
              {title}
            </div>
          )}
          {description && <div className="body-1">{description}</div>}
          {linkUrl && (
            <Link href={linkUrl} legacyBehavior>
              <span className="body-1 cursor-pointer underline">
                {linkText}
              </span>
            </Link>
          )}
        </div>
      )}
    </>
  )
}

export default VideoPlayer
